import React, { useState, useEffect } from 'react';
import { 
  ArrowTrendingUpIcon as TrendingUp, 
  ArrowTrendingDownIcon as TrendingDown,
  ClockIcon as Clock,
  MapPinIcon as MapPin,
  UsersIcon as Users,
  StarIcon as Star,
  CalendarIcon as Calendar,
  ChartBarIcon as BarChart3,
  XCircleIcon as XCircle,
  CheckCircleIcon as CheckCircle,
  BoltIcon as Zap,
  ViewfinderCircleIcon as Target,
  ArrowPathIcon as RefreshCw,
  CurrencyDollarIcon as DollarSign
} from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../../config/api';

interface DynamicPricing {
  base_price: number;
  final_price_per_hour: number;
  total_price: number;
  duration_hours: number;
  total_multiplier: number;
  breakdown: {
    time_multiplier: number;
    seat_multiplier: number;
    demand_multiplier: number;
    event_multiplier: number;
  };
  time_type: string;
  seat_category: string;
  demand_level: string;
  discount_percentage: number;
  surcharge_percentage: number;
  calculated_at: string;
}

interface PricingForecast {
  hour: number;
  datetime: string;
  price_per_hour: number;
  time_type: string;
  demand_level: string;
  discount_percentage: number;
  surcharge_percentage: number;
}

interface OptimalPackage {
  duration: string;
  hours: number;
  base_price: number;
  discount_rate: number;
  final_price: number;
  savings: number;
  value_score: number;
  recommended: boolean;
  recommendation_reason?: string;
}

interface DynamicPricingDisplayProps {
  selectedSeat?: string;
  durationHours?: number;
  onPriceUpdate?: (pricing: DynamicPricing) => void;
}

const DynamicPricingDisplay: React.FC<DynamicPricingDisplayProps> = ({
  selectedSeat,
  durationHours = 1,
  onPriceUpdate
}) => {
  const [currentPricing, setCurrentPricing] = useState<DynamicPricing | null>(null);
  const [forecast, setForecast] = useState<PricingForecast[]>([]);
  const [packages, setPackages] = useState<OptimalPackage[]>([]);
  const [currentStatus, setCurrentStatus] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'current' | 'forecast' | 'packages'>('current');

  useEffect(() => {
    loadPricingData();
    const interval = setInterval(loadPricingData, 60000); // 1분마다 업데이트
    return () => clearInterval(interval);
  }, [selectedSeat, durationHours]);

  const loadPricingData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await Promise.all([
        loadCurrentPricing(),
        loadForecast(),
        loadPackages(),
        loadCurrentStatus()
      ]);
    } catch (err) {
      setError('가격 정보를 불러오는 중 오류가 발생했습니다');
      console.error('Failed to load pricing data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadCurrentPricing = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams();
      if (selectedSeat) params.append('seat_id', selectedSeat);
      params.append('duration_hours', durationHours.toString());

      const response = await fetch(`${API_BASE_URL}/api/v1/pricing/dynamic/calculate?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCurrentPricing(data.pricing);
          onPriceUpdate?.(data.pricing);
        }
      }
    } catch (err) {
      console.error('Failed to load current pricing:', err);
    }
  };

  const loadForecast = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams();
      if (selectedSeat) params.append('seat_id', selectedSeat);
      params.append('hours_ahead', '12');

      const response = await fetch(`${API_BASE_URL}/api/v1/pricing/dynamic/forecast?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setForecast(data.forecast.forecast_data || []);
        }
      }
    } catch (err) {
      console.error('Failed to load forecast:', err);
    }
  };

  const loadPackages = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams();
      if (selectedSeat) params.append('seat_id', selectedSeat);

      const response = await fetch(`${API_BASE_URL}/api/v1/pricing/dynamic/packages?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setPackages(data.packages || []);
        }
      }
    } catch (err) {
      console.error('Failed to load packages:', err);
    }
  };

  const loadCurrentStatus = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/pricing/dynamic/current-status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCurrentStatus(data.current_status);
        }
      }
    } catch (err) {
      console.error('Failed to load current status:', err);
    }
  };

  const getTimeTypeIcon = (timeType: string) => {
    switch (timeType) {
      case 'peak': return <TrendingUp className="h-4 w-4 text-red-500" />;
      case 'night': return <TrendingDown className="h-4 w-4 text-green-500" />;
      case 'weekend': return <Calendar className="h-4 w-4 text-purple-500" />;
      default: return <Clock className="h-4 w-4 text-blue-500" />;
    }
  };

  const getTimeTypeLabel = (timeType: string) => {
    switch (timeType) {
      case 'peak': return '피크시간';
      case 'night': return '심야시간';
      case 'weekend': return '주말';
      case 'normal': return '일반시간';
      default: return timeType;
    }
  };

  const getSeatCategoryIcon = (category: string) => {
    switch (category) {
      case 'premium': return <Star className="h-4 w-4 text-yellow-500" />;
      case 'economy': return <Target className="h-4 w-4 text-green-500" />;
      default: return <MapPin className="h-4 w-4 text-blue-500" />;
    }
  };

  const getDemandIcon = (level: string) => {
    switch (level) {
      case 'high': return <Users className="h-4 w-4 text-red-500" />;
      case 'medium': return <Users className="h-4 w-4 text-yellow-500" />;
      default: return <Users className="h-4 w-4 text-green-500" />;
    }
  };

  const renderCurrentPricing = () => (
    <div className="space-y-6">
      {currentPricing && (
        <>
          {/* 현재 가격 */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-gray-900">현재 동적 가격</h4>
              <div className="flex items-center text-sm text-gray-600">
                <Clock className="h-4 w-4 mr-1" />
                {new Date(currentPricing.calculated_at).toLocaleTimeString()}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <span className="text-sm text-gray-600">기본 요금:</span>
                <div className="text-lg font-medium">{currentPricing.base_price.toLocaleString()}원/시간</div>
              </div>
              <div>
                <span className="text-sm text-gray-600">최종 요금:</span>
                <div className="text-2xl font-bold text-blue-600">
                  {currentPricing.final_price_per_hour.toLocaleString()}원/시간
                </div>
              </div>
            </div>

            {durationHours > 1 && (
              <div className="bg-white rounded-lg p-3 border border-blue-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">{durationHours}시간 총 요금:</span>
                  <span className="text-xl font-bold text-blue-600">
                    {currentPricing.total_price.toLocaleString()}원
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* 가격 구성 요소 */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <h4 className="font-medium text-gray-900 mb-3">가격 구성 요소</h4>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {getTimeTypeIcon(currentPricing.time_type)}
                  <span className="ml-2 text-sm">{getTimeTypeLabel(currentPricing.time_type)}</span>
                </div>
                <span className="text-sm font-medium">
                  ×{currentPricing.breakdown.time_multiplier}
                </span>
              </div>
              
              {selectedSeat && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {getSeatCategoryIcon(currentPricing.seat_category)}
                    <span className="ml-2 text-sm">
                      {currentPricing.seat_category === 'premium' ? '프리미엄 좌석' :
                       currentPricing.seat_category === 'economy' ? '이코노미 좌석' : '일반 좌석'}
                    </span>
                  </div>
                  <span className="text-sm font-medium">
                    ×{currentPricing.breakdown.seat_multiplier}
                  </span>
                </div>
              )}
              
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {getDemandIcon(currentPricing.demand_level)}
                  <span className="ml-2 text-sm">
                    {currentPricing.demand_level === 'high' ? '높은 수요' :
                     currentPricing.demand_level === 'medium' ? '보통 수요' : '낮은 수요'}
                  </span>
                </div>
                <span className="text-sm font-medium">
                  ×{currentPricing.breakdown.demand_multiplier}
                </span>
              </div>
            </div>
            
            <div className="mt-4 pt-3 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">총 배수:</span>
                <span className="text-lg font-bold">
                  ×{currentPricing.total_multiplier}
                </span>
              </div>
            </div>
          </div>

          {/* 할인/할증 정보 */}
          {(currentPricing.discount_percentage > 0 || currentPricing.surcharge_percentage > 0) && (
            <div className={`rounded-lg p-4 border ${
              currentPricing.discount_percentage > 0 
                ? 'bg-green-50 border-green-200' 
                : 'bg-orange-50 border-orange-200'
            }`}>
              <div className="flex items-center">
                {currentPricing.discount_percentage > 0 ? (
                  <>
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                    <span className="font-medium text-green-900">
                      {currentPricing.discount_percentage}% 할인 적용!
                    </span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-5 w-5 text-orange-600 mr-2" />
                    <span className="font-medium text-orange-900">
                      {currentPricing.surcharge_percentage}% 할증 적용
                    </span>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderForecast = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-900">12시간 가격 예측</h4>
        <div className="text-sm text-gray-600">
          시간당 요금 기준
        </div>
      </div>
      
      <div className="grid grid-cols-1 gap-3 max-h-96 overflow-y-auto">
        {forecast.map((item, index) => (
          <div 
            key={index} 
            className={`p-3 rounded-lg border ${
              index === 0 ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {getTimeTypeIcon(item.time_type)}
                <div className="ml-3">
                  <div className="text-sm font-medium">
                    {index === 0 ? '지금' : `${item.hour}시간 후`}
                  </div>
                  <div className="text-xs text-gray-600">
                    {new Date(item.datetime).toLocaleTimeString([], { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </div>
                </div>
              </div>
              
              <div className="text-right">
                <div className="text-lg font-bold">
                  {item.price_per_hour.toLocaleString()}원
                </div>
                {item.discount_percentage > 0 && (
                  <div className="text-xs text-green-600">
                    {item.discount_percentage}% 할인
                  </div>
                )}
                {item.surcharge_percentage > 0 && (
                  <div className="text-xs text-orange-600">
                    {item.surcharge_percentage}% 할증
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {currentStatus && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h5 className="font-medium text-blue-900 mb-2">💡 절약 팁</h5>
          <p className="text-sm text-blue-800">
            {currentStatus.best_deal_today.savings > 0 ? (
              <>
                <span className="font-medium">
                  {new Date(currentStatus.best_deal_today.datetime).toLocaleTimeString([], { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </span>에 예약하면 시간당 <span className="font-medium">
                  {currentStatus.best_deal_today.savings.toLocaleString()}원
                </span> 절약할 수 있습니다!
              </>
            ) : (
              '현재가 가장 저렴한 시간대입니다!'
            )}
          </p>
        </div>
      )}
    </div>
  );

  const renderPackages = () => (
    <div className="space-y-4">
      <h4 className="font-medium text-gray-900">최적 이용권 패키지</h4>
      
      <div className="grid grid-cols-1 gap-3">
        {packages.map((pkg, index) => (
          <div 
            key={index}
            className={`p-4 rounded-lg border-2 transition-all ${
              pkg.recommended 
                ? 'border-blue-500 bg-blue-50 shadow-md' 
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <span className="font-medium text-lg">{pkg.duration}</span>
                {pkg.recommended && (
                  <span className="ml-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                    추천
                  </span>
                )}
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-blue-600">
                  {pkg.final_price.toLocaleString()}원
                </div>
                {pkg.savings > 0 && (
                  <div className="text-sm text-green-600">
                    {pkg.savings.toLocaleString()}원 절약
                  </div>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">정가:</span>
                <div className="font-medium">{pkg.base_price.toLocaleString()}원</div>
              </div>
              <div>
                <span className="text-gray-600">할인율:</span>
                <div className="font-medium">{(pkg.discount_rate * 100).toFixed(0)}%</div>
              </div>
            </div>
            
            <div className="mt-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">시간당 단가:</span>
                <span className="font-medium">
                  {Math.round(pkg.final_price / pkg.hours).toLocaleString()}원
                </span>
              </div>
            </div>
            
            {pkg.recommended && pkg.recommendation_reason && (
              <div className="mt-3 text-sm text-blue-700 bg-blue-100 rounded p-2">
                💡 {pkg.recommendation_reason}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Zap className="h-6 w-6 text-yellow-500 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">동적 가격 정책</h3>
        </div>
        <button
          onClick={loadPricingData}
          disabled={isLoading}
          className="text-gray-500 hover:text-gray-700"
        >
          <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {selectedSeat && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <div className="text-sm text-gray-600">선택된 좌석</div>
          <div className="font-medium">{selectedSeat}</div>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-center">
            <XCircle className="h-5 w-5 text-red-500 mr-2" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* 탭 메뉴 */}
      <div className="flex space-x-1 mb-6 bg-gray-100 rounded-lg p-1">
        {[
          { id: 'current', label: '현재 가격', icon: DollarSign },
          { id: 'forecast', label: '가격 예측', icon: BarChart3 },
          { id: 'packages', label: '패키지', icon: Star }
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id as any)}
            className={`flex-1 flex items-center justify-center py-2 px-3 rounded-md text-sm font-medium transition-colors ${
              activeTab === id
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <Icon className="h-4 w-4 mr-2" />
            {label}
          </button>
        ))}
      </div>

      {/* 탭 콘텐츠 */}
      <div>
        {activeTab === 'current' && renderCurrentPricing()}
        {activeTab === 'forecast' && renderForecast()}
        {activeTab === 'packages' && renderPackages()}
      </div>
    </div>
  );
};

export default DynamicPricingDisplay;